import { uniqBy } from 'lodash';
import { difference, intersection, union } from 'ramda';
import React, { FC, forwardRef, useCallback } from 'react';
import { ProjectId } from 'api/models';
import { MemberType, Role, Team } from 'api/models/core-types';
import { FieldOptionBase } from 'api/tasks';
import { Option } from 'components/atoms/select/types';
import {
  AssigneePickerFilter,
  useAssigneeOptions,
} from 'hooks/use-assignee-options';
import { useIsRestrictedPartnerUser } from 'hooks/use-partner';
import { Translate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import {
  AssigneeField,
  AssigneeValue,
  ProjectUsersByAccess,
  TeamId,
  UserId,
  UsersMetadata,
} from '../fields';
import { EditableUserRelatedFieldProps } from './editable-props';
import { NoValue } from './no-value';
import { StyledSelect } from './styled-select';

interface Props extends EditableUserRelatedFieldProps<AssigneeValue[]> {
  field: AssigneeField;
}

interface OptionGroup {
  id?: string;
  label: string;
  options: Option[];
}

const empty: ProjectUsersByAccess = {
  private: {
    in_project_user_ids: [],
    not_in_project_user_ids: [],
  },
  shared: {
    in_project_user_ids: [],
    not_in_project_user_ids: [],
  },
};

export const getUserOptions = (
  translate: Translate,
  users: UsersMetadata,
  [, project_id]: ProjectId,
  is_private: boolean,
): OptionGroup[] => {
  const data = users.by_project_id[project_id] ?? empty;
  const { in_project_user_ids, not_in_project_user_ids } = is_private
    ? data.private
    : data.shared;

  return [
    {
      label: translate('users'),
      options: in_project_user_ids
        .filter((u) => users.active_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
    {
      label: translate('not_in_project'),
      options: not_in_project_user_ids
        .filter((u) => users.active_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
  ];
};
type PlaceholderListControlMeta =
  | { showOnlyTeamsAssignedToTheirCompany: false }
  | { showOnlyTeamsAssignedToTheirCompany: true; companyId: number };
export const getOptionsCombined = (
  translate: Translate,
  users: UsersMetadata,
  teams: Team[],
  project_ids: ProjectId[],
  is_private: boolean,
  roles?: Role[],
  placeholderListControl: PlaceholderListControlMeta = {
    showOnlyTeamsAssignedToTheirCompany: false,
  },
): OptionGroup[] => {
  const roleMap: { [id: number]: Role } | undefined = roles
    ? roles?.reduce(
        (prev, _role) => ({
          ...prev,
          [_role.roleId]: _role,
        }),
        {},
      )
    : undefined;
  // This is the final list of users who can be assigned to a task
  const assignable_in_all_projects = project_ids.reduce(
    (previous, [, project_id]) => {
      const data = users.by_project_id[project_id] ?? empty;
      const match = is_private ? data.private : data.shared;

      return intersection(
        previous,
        union(match.in_project_user_ids, match.not_in_project_user_ids),
      );
    },
    users.active_user_ids,
  );
  const in_project_user_ids = project_ids.reduce((previous, [, project_id]) => {
    const data = users.by_project_id[project_id] ?? empty;
    const match = is_private ? data.private : data.shared;

    return intersection(previous, match.in_project_user_ids);
  }, assignable_in_all_projects);

  const not_in_project_user_ids = difference(
    assignable_in_all_projects,
    in_project_user_ids,
  );

  // * Creating a list of unique teams because:
  // * 1. in TeamsStore we have account-level placeholders
  // * 2. in membership, we have placeholders associated to project, including account-level placeholders, hence duplicates

  const uniqueTeams = uniqBy(teams, 'teamId');

  return [
    {
      id: 'users',
      label: translate('user_in_project'),
      options: in_project_user_ids
        .filter((u) => users.active_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => {
          return {
            value: AssigneeValue.toString(UserId(u.userId)),
            label: fullName(u),
            imageUrl: u.photoUrl,
            description: u.role?.roleName,
            color: getRandomColor(u.userId),
            roleId: u.role?.roleId,
            extra: {
              id: u.userId,
              type: 'user',
              timeOffDetails: u?.userTimeOffDetails,
            },
          };
        })
        .sort(byLabel),
    },
    {
      id: 'teams',
      label: translate('placeholders'),
      options: (uniqueTeams ?? [])
        .filter((t) => {
          if (is_private && t.teamType === MemberType.External) {
            return false;
          }
          if (
            placeholderListControl.showOnlyTeamsAssignedToTheirCompany &&
            !t.companyList?.find(
              ({ companyId }) => companyId === placeholderListControl.companyId,
            )
          ) {
            return false;
          }

          return true;
        })
        .map((t) => ({
          value: AssigneeValue.toString(TeamId(t.teamId)),
          label: t.teamName,
          description: t.roleId
            ? roleMap?.[t.roleId]?.roleName ?? t.teamDescription
            : undefined,
          color: getRandomColor(t.teamId),
          roleId: t.roleId,
          extra: {
            id: t.teamId,
            type: 'team',
            teamType: t.teamType,
          },
        }))
        .sort(byLabel),
    },
    {
      id: 'users_not_in_project',
      label: translate('not_in_project'),
      options: not_in_project_user_ids
        .filter((u) => users.active_user_ids.includes(u))
        .map((u) => users.by_user_id[u])
        .filter(Boolean)
        .map((u) => {
          return {
            value: AssigneeValue.toString(UserId(u.userId)),
            label: fullName(u),
            imageUrl: u.photoUrl,
            description: u.role?.roleName,
            color: getRandomColor(u.userId),
            extra: {
              id: u.userId,
              type: 'user',
              timeOffDetails: u?.userTimeOffDetails,
            },
          };
        })
        .sort(byLabel),
    },
  ];
};

export const useOptionsForAssigneePicker = (): ((
  translate: Translate,
  users: UsersMetadata,
  teams: Team[],
  project_id: ProjectId,
  is_private: boolean,
  roles?: Role[],
) => OptionGroup[]) => {
  const isRestrictedPartnerUser = useIsRestrictedPartnerUser();
  const {
    user: {
      company: { companyId },
    },
  } = useUser();

  return useCallback(
    (
      translate: Translate,
      users: UsersMetadata,
      teams: Team[],
      project_id: ProjectId,
      is_private: boolean,
      roles?: Role[],
    ): OptionGroup[] =>
      getOptionsCombined(
        translate,
        users,
        teams,
        [project_id],
        is_private,
        roles,
        isRestrictedPartnerUser
          ? { showOnlyTeamsAssignedToTheirCompany: true, companyId }
          : { showOnlyTeamsAssignedToTheirCompany: false },
      ),
    [companyId, isRestrictedPartnerUser],
  );
};

export const AssigneeFieldValue: FC<Props> = forwardRef(
  ({ field, is_private, project_id, editable, onChange }, ref) => {
    const options = useAssigneeOptions({
      projectId: project_id,
      isPrivate: is_private,
      filter: AssigneePickerFilter.All,
    });

    const flattened = options.reduce(
      (prev, x) => prev.concat(x.options),
      [] as Option[],
    );
    const assignees = (field.value ?? [])
      .map(AssigneeValue.toString)
      .map((x) => flattened.find((o) => o.value === x)!)
      .filter(Boolean);

    return !editable ? (
      field.value == null ? (
        <NoValue />
      ) : (
        <>{field.value}</>
      )
    ) : (
      <StyledSelect
        ref={ref}
        type="user"
        isMulti
        isClearable
        menuIsOpen
        options={options}
        value={assignees.sort(byLabel)}
        onChange={(latest: FieldOptionBase[]) =>
          onChange?.(
            (latest ?? [])
              .sort(byLabel)
              .map((x) => x.value.toString())
              .map(AssigneeValue.fromString),
          )
        }
        hideSelectedOptions={false}
      />
    );
  },
);
